---
title: "What Is USDT and Why We Trade Against It"
slug: what-is-usdt
shortTitle: "What Is USDT?"
description: "USDT is the dollar-pegged stablecoin behind every balance and price on the platform. Learn how its peg works, what the TRC20, ERC20 and BEP20 networks mean, and its risks."
level: beginner
category: crypto
course: beginner
order: 3
topics: [stablecoins, ethereum]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/what-is-usdt.svg
faqs:
  - q: "Is 1 USDT always worth exactly 1 US dollar?"
    a: "It aims to be, and it usually trades very close to one dollar. During periods of stress it has briefly traded a little below, so it is best thought of as a dollar-pegged token rather than a dollar itself. On the platform, 1 USDT is treated as $1 for balances and prices."
  - q: "What is the difference between USDT on TRC20, ERC20 and BEP20?"
    a: "It is the same token issued on three different blockchains: TRON, Ethereum and BNB Smart Chain. Each has its own address format and network fee, and a transfer only works if the sender and the receiver use the same network."
  - q: "What happens if I send USDT on the wrong network?"
    a: "The funds may not arrive and may be impossible to recover, because blockchain transfers cannot be reversed. Always choose the network shown on the Deposit page, and contact support straight away if a mistake happens."
  - q: "Do I need USDT to open an account?"
    a: "No. You can also deposit by wire transfer or card where available. Your trading account itself is kept in USDT, so balances, margin and profit or loss are always shown in USDT."
  - q: "Can I withdraw USDT on BEP20?"
    a: "BEP20 can be used for deposits but is not offered for withdrawals. For USDT withdrawals you can choose TRC20 or ERC20, and you can also withdraw in BTC or ETH, by bank wire or as a card refund where eligible."
---

On {{BRAND}}, your balance, your margin and every profit or loss are counted in **USDT**. Bitcoin is quoted as BTC/USDT, gold as GOLD/USDT, and the dollar sign you see next to your balance simply means 1 USDT is treated as $1. So it is worth knowing exactly what USDT is.

This lesson explains what USDT is, how it holds its value, why trading platforms use it, how to read a price "against USDT", and the network choices that matter every time you move it.

<CTA label="Deposit USDT" to="/account/deposit" />

<KeyTakeaways>

- **USDT is a dollar-pegged token.** It is a stablecoin issued by Tether that aims to be worth one US dollar and moves on several blockchains.
- **"Against USDT" means priced in USDT.** BTC/USDT shows how many USDT one bitcoin costs, and every result on {{BRAND}} is settled in USDT.
- **The network matters as much as the coin.** TRC20, ERC20 and BEP20 are the same token on different blockchains; sending on the wrong one can lose your funds.
- **Stable does not mean without risk.** USDT depends on its issuer's reserves and on market confidence, and it has briefly slipped below one dollar before.

</KeyTakeaways>

## What USDT is

**USDT**, often called Tether, is a **stablecoin**: a crypto token designed to keep a steady value, in this case one US dollar. It launched in 2014 and has become the most widely used stablecoin in crypto trading.

USDT is issued by a company, Tether. When an approved customer sends dollars to Tether, new USDT is created for them; when they return USDT, it is taken out of circulation and they receive dollars back. Tether says the tokens are backed by reserves such as cash, short-term US government debt and other assets, and it publishes regular reports on those reserves.

USDT is not a bank deposit and is not protected by deposit insurance. Its value rests on the issuer's reserves and on the market's trust that one token can be swapped for one dollar.

## How the dollar peg holds

The peg is held in place by large traders looking for a small profit from any gap:

- If USDT trades **below** one dollar on exchanges, large holders who can redeem with the issuer buy it cheaply and hand it back for a full dollar. That buying pushes the price back up.
- If USDT trades **above** one dollar, they create new tokens for one dollar each and sell them for more. That selling pulls the price back down.

This works as long as the market believes redemptions will be honored. During sharp market stress, such as in May 2022, USDT has briefly traded a few cents below one dollar before recovering.

## Why trading platforms use USDT

USDT gives a crypto-based platform the stability of the dollar with the speed of a blockchain:

1. **A steady unit of account.** Your balance does not rise and fall with Bitcoin, so profit and loss are easy to read.
2. **Fast, round-the-clock transfers.** Blockchain transfers settle on any day of the week, without waiting for banking hours.
3. **Wide availability.** Most crypto exchanges and wallets support it.
4. **One currency for every market.** Gold, Apple shares, EUR/USD and Bitcoin all settle in the same USDT balance.

Wire transfer and card deposits are also available as requests in USD, GBP or EUR, while your trading account itself is always kept in USDT.

{/* TODO: verify with platform — how wire and card deposits in USD, GBP or EUR are converted and credited to the USDT balance. */}

## Reading a price "against USDT"

A trading pair has two parts. The first is the **base**, the thing being priced. The second is the **quote**, the currency the price is written in. BTC/USDT at 60,000 means one bitcoin costs 60,000 USDT. GOLD/USDT at 2,400 means one ounce of gold costs 2,400 USDT.

Forex pairs keep their real names, such as EUR/USD, and stocks and indices use their usual symbols, such as AAPL or SPX500. They all still settle in USDT, so your profit or loss always lands in the same balance.

<Example title="An ETH/USDT trade settled in USDT (illustrative prices)">

Your balance is 1,000 USDT. You buy an ETH/USDT position worth 1,500 USDT at 3,000, which is 1,500 ÷ 3,000 = 0.5 ETH.

ETH rises to 3,120 and you close: (3,120 − 3,000) × 0.5 = **+60 USDT**.

Your balance becomes 1,000 + 60 = **1,060 USDT**, before the spread, the fee and any swap. You never held any ETH; the result simply arrives in USDT.

</Example>

A position bigger than your balance, like the one above, is possible because of leverage, which you will meet in [Leverage and Margin Explained Simply](/academy/article/leverage-and-margin).

## The three USDT networks

USDT exists on several blockchains at once. Each version is the same token, but it lives on a different network with its own addresses and fees. **ERC20** is the token standard used on Ethereum, which is why moving USDT on Ethereum costs a network fee, called gas, paid in ETH. **TRC20** is the equivalent on TRON, and **BEP20** on BNB Smart Chain.

<CompareTable caption="USDT networks on {{BRAND}}">

| Network | Blockchain | Address starts with | Network fee paid in | Deposit | Withdraw |
| --- | --- | --- | --- | --- | --- |
| TRC20 | TRON | T | TRX | Yes | Yes |
| ERC20 | Ethereum | 0x | ETH | Yes | Yes |
| BEP20 | BNB Smart Chain | 0x | BNB | Yes | No |

</CompareTable>

Network fees are charged by the blockchain and paid by whoever sends the transfer. They change with how busy each network is, and Ethereum is often the most expensive of the three. Withdrawals from {{BRAND}} carry {{WITHDRAWAL_FEE}}.

<Warning>

ERC20 and BEP20 addresses look identical: both start with 0x. Sending USDT on one network to an address that expects the other may mean the funds never arrive and cannot be recovered. Always choose the network that the Deposit page shows for the address you are using, on both the sending and the receiving side.

</Warning>

## Depositing USDT on {{BRAND}}

<Steps>
<Step title="Open the Deposit page">

Use the **Deposit** button in the top bar or go to [Account → Deposit](/account/deposit), then choose the **Crypto** tab.

</Step>
<Step title="Pick USDT and a network">

Choose USDT on TRC20, ERC20 or BEP20. You may instead be taken to a payment page with an address, a QR code and a timer. Make sure you send at least {{MIN_DEPOSIT}}.

</Step>
<Step title="Copy the address carefully">

Copy the address or scan the QR code, then check the first and last few characters against what you pasted into your wallet or exchange.

</Step>
<Step title="Send on the same network">

In your wallet or exchange, choose the same network you selected on {{BRAND}}, then send. Your balance updates once the blockchain has confirmed the transfer.

</Step>
</Steps>

<ProTip>

Double-check your withdrawal address when you save it: the coin and network must match the wallet you are sending to. [Account Setup, Verification and Deposits](/academy/article/account-setup-deposits) covers the whole process.

</ProTip>

## The risks of holding USDT

USDT is designed to be stable, but it is not risk-proof:

- **Peg risk.** It can trade below one dollar during stress, as it briefly has before.
- **Issuer risk.** Its value depends on Tether's reserves and on redemptions being honored.
- **Regulatory risk.** Rules for stablecoins differ by country and continue to change.
- **Freezes.** The issuer can freeze tokens held at specific addresses, usually at the request of authorities.
- **Transfer mistakes.** A transfer sent to the wrong address or network cannot be reversed.

Your {{BRAND}} balance is counted in USDT and shown with a $ sign because the platform treats 1 USDT as $1. If USDT's market price ever moved away from one dollar, the real-world value of a USDT balance would move with it.

## Conclusion

USDT is a stablecoin that aims to hold one US dollar, backed by its issuer's reserves and kept close to its peg by traders who profit from any gap. {{BRAND}} uses it so that every market, from Bitcoin to gold, settles into one steady balance. When you read BTC/USDT, read it as "the price of one bitcoin in USDT". When you move USDT, check the network as carefully as the address, because that is where most expensive mistakes happen.
